import { Injectable } from '@angular/core';
import { IHotsheetPropertyInfo } from '../interfaces';
import { HotsheetService } from './hotsheet.service';
import { ToastService } from './toast.service';
import { ErrorHandlerService } from './error-handler.service';
import Papa from 'papaparse';
import * as Parse from 'parse';

@Injectable()
export class CsvImportService {
    public isOnHotsheet = false;

    constructor(
        private hotsheetService: HotsheetService,
        private toast: ToastService,
        private errorHandler: ErrorHandlerService
    ) {
        this.complete = this.complete.bind(this);
        this.extractFields = this.extractFields.bind(this);
        this.save = this.save.bind(this);
    }

    parseCSV(file: File, isOnHotsheet: boolean = false, importRecord: Parse.Object) {
        this.isOnHotsheet = isOnHotsheet;

        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                complete: async (results: Papa.ParseResult<any>) => {
                    const importResults = await this.complete(results, importRecord);
                    resolve(importResults);
                },
                error: reject,
                skipEmptyLines: true,
                transformHeader: (header: string): string => {
                    return header.toLowerCase().trim();
                },
                transform: (value: string): string => {
                    return value.toLowerCase().trim();
                }
            });
        });
    }

    extractFields(parsedCSV: any) {
        const propertyInfo: IHotsheetPropertyInfo | any = {};

        // Extract Street Address
        if (parsedCSV.address) {
            propertyInfo.address = parsedCSV.address;
        } else if (parsedCSV['street name']) {
            propertyInfo.address = parsedCSV['street #'] + ' ' + parsedCSV['street name'] + ' ' + parsedCSV['street suffix'];
        }

        // Extract Address Line 2
        propertyInfo.address2 = (parsedCSV['unit #'] ? '#' + parsedCSV['unit #'] : undefined);

        // Extract City
        propertyInfo.city = parsedCSV.city;

        // Extract State
        propertyInfo.state = parsedCSV.state || parsedCSV['state/province'];

        // Extract Zip
        propertyInfo.zip = parsedCSV.zip || parsedCSV['zip code'];

        // MLS ID
        propertyInfo.mlsId = parsedCSV['ml number'] || parsedCSV['list number'];

        // MLS Status
        propertyInfo.mlsStatus = parsedCSV.status;

        // Listing Agent info
        propertyInfo.listingAgentName = parsedCSV['list agent'] || parsedCSV['listing agent'];
        propertyInfo.listingAgentCell = parsedCSV['listing agy phone'] || parsedCSV['agency phone'] || parsedCSV['list agent cell'];

        // Listing Office
        propertyInfo.listingOffice = parsedCSV['list office'];

        // Extract Co Listing Agent
        propertyInfo.coListingAgentName = parsedCSV['co-listing agent'] || undefined;

        // Extract Beds
        propertyInfo.beds_count = parsedCSV['beds'];

        // Extract Baths
        propertyInfo.baths = parsedCSV['full baths'];

        // Extract Year Built
        propertyInfo.year_built = parsedCSV['year built'];

        // Extract Pool
        propertyInfo.pool_type = parsedCSV['pool'];

        // Extract Zoning
        propertyInfo.standardized_land_use_category = parsedCSV['property style'];

        return propertyInfo;
    }

    async complete(results: Papa.ParseResult<any>, importRecord: Parse.Object) {
        let missedCount: number = 0;
        let propertyData: any[] = results.data.map(this.extractFields);
        const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

        propertyData = propertyData.filter((property: any) => {
            if (!property.address) {
                missedCount++;
                return false;
            }

            return true;
        });

        // Google Location Sanitation is used to fill in missing info and format abbreviations correctly
        for (let property of propertyData) {
            let address: string = '';

            // Slowing requests to stay within Google's Query Limit
            await delay(100);

            try {
                address = property.address + ', ' + property.city + ', ' + property.zip + ', ' + (property.state || '');
                property = await this.hotsheetService.getPlaceByGoogleSearch(address);
            } catch (error) {
                console.log(`Failed to parse address ${address}`);
                console.error(error);
            }
        }

        try {
            const promises: Promise<Parse.Object>[] = propertyData.map((propertyInfo: IHotsheetPropertyInfo) => this.save(propertyInfo, importRecord));
            await Promise.all(promises);

            importRecord.set('propertyCount', propertyData.length);
            importRecord.set('failedCount', missedCount);
            importRecord.set('fromHotsheet', this.isOnHotsheet);

            await importRecord.save();
        } catch (error) {
            console.error(error);
            this.errorHandler.handleInvalidSessionError(error);
        }

        return {
            records: propertyData.length,
            failed: missedCount
        };
    }

    /**
     * Checks if property already exists for this user, if so will simply add the
     * property back to the user's hotsheet rather than re-creating it.
     */
    async checkIfPropertyAlreadyExists(property: IHotsheetPropertyInfo) {
        try {
            const existing: Parse.Object = (await this.hotsheetService.checkIfPropertyAlreadyExists(property)) as Parse.Object;

            if (!existing) {
                return false;
            }

            if (this.isOnHotsheet) {
                existing.set('isOnHotsheet', true);
                existing.set('lastHotsheetImportDate', new Date());
                return existing.save();
            }

            return existing;
        } catch (error: any) {
            console.error(error);
            this.errorHandler.handleInvalidSessionError(error);
            void this.toast.showError(error.message);

            return false;
        }
    }

    async save(original: IHotsheetPropertyInfo, importRecord: Parse.Object) {
        // Check if property already exists
        const existing = await this.checkIfPropertyAlreadyExists(original);

        if (existing) {
            existing.set('mlsId', original.mlsId);
            existing.set('listingAgent', original.listingAgentName);
            existing.set('listingOffice', original.listingOffice);
            existing.set('listingAgentCell', original.listingAgentCell);
            existing.set('coListingAgent', original.coListingAgentName);
            existing.set('coListingAgentCell', original.coListingAgentCell);

            const importUpdateRecords = existing.get('importUpdateRecords') || [];
            importUpdateRecords.push(importRecord);

            existing.set('importUpdateRecords', importUpdateRecords);

            return existing.save();
        } else {
            const property: Parse.Object = await this.hotsheetService.save(original, this.isOnHotsheet);
            property.set('importRecord', importRecord);
            this.hotsheetService.setPointersOnChildObjects(property);
    
            // Resave the property to cascade save the pointer changes
            return property.save();
        }
    }

    createPropertyImport(source: 'csv', fromHotsheet: boolean, file?: File) {
        const object: Parse.Object = new Parse.Object('PropertyImport');

        if (source === 'csv' && file) {
            const parseFile: Parse.File = new Parse.File((file?.name as string).replace(/[^a-zA-Z0-9]/g,'_'), file as File);
            object.set('csv', parseFile);
        }

        object.set('source', source);
        object.set('fromHotsheet', fromHotsheet);

        return object.save();
    }
}
